import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EditSite from "./EditSite";
import "./ViewSite.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiService from "../../utils/apiservice";

const ViewSites = () => {
  const [rows, setRows] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteError, setDeleteError] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const latestRequest = React.useRef(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Core API function to pull individual customer data 
  const fetchSites = async (customerId) => {
    const requestId = ++latestRequest.current;

    console.log("🚀 fetchSites called with customerId:", customerId);
    if (!customerId) {
      console.warn("⚠️ fetchSites aborted: customerId is null or undefined");
      return;
    }

    try {


      let response;

    // 🔥 ALL customers → assignment API (flatten everything)
    if (customerId === "all") {
      response = await apiService({
        url: `/api/get_sites_for_assignment/`,
        method: "GET",
        params: {},
      });

      // const assigned = response?.data?.assigned_sites || [];
      // const unassigned = response?.data?.unassigned_sites || [];

      // // 🔥 FLATTEN BOTH INTO ONE LIST (NO LOGIC DIFFERENCE)
      // const merged = [...assigned, ...unassigned];

      // const formattedData = merged.map((site, index) => ({
      //   id: index + 1,
      //   site_id: site.site_id,
      //   name: site.name,
      //   survey_no: site.survey_no,
      //   pin_code: site.pin_code,
      //   society_reg_no: site.society_reg_no,
      //   address: site.address,
      //   latitude: site.latitude,
      //   longitude: site.longitude,
      //   asset_id: site.asset_id,
      // }));

      // setRows(formattedData);
       if (requestId !== latestRequest.current) return;

      const merged = [
        ...(response?.data?.assigned_sites || []),
        ...(response?.data?.unassigned_sites || [])
      ];

      setRows(merged.map((site, index) => ({
        id: index + 1,
        ...site
      })));
      return;
    }


      response = await apiService({
        url: `/api/get_customer_sites/${customerId}/`,
        method: "GET",
        data: {}, 
      });

      console.log("Site API Response:", response);
      const sitesList = response?.data?.data || response?.data || [];

      const formattedData = sitesList.map((site, index) => ({
        id: index + 1,
        site_id: site.site_id,
        name: site.name,
        survey_no: site.survey_no,
        pin_code: site.pin_code,
        society_reg_no: site.society_reg_no,
        address: site.address,
        latitude: site.latitude,
        longitude: site.longitude,
        asset_id: site.asset_id,
      }));

      setRows(formattedData);
    } catch (error) {
      console.error("❌ Error fetching sites:", error);
      setRows([]);
    }
  };

  // 1. Initial Load: Get Customers
  const fetchCustomers = async () => {
    try {
      const response = await apiService({
        url: "/api/get_customers/",
        method: "GET",
        data: {},
      });

      console.log("Customer API Response:", response);

      const customerData = response?.data?.data || response?.data || [];
      setCustomers(customerData);

      if (customerData.length > 0) {
        // Fallback checks for key variations: customer_id, id, or customer_id string parsed
       const initialId = customerData[0].tb_customer_id;
        console.log("🎯 Setting initial selected customer ID to:", initialId);
        
        setSelectedCustomer(initialId);
        // Force an immediate API execution cascade right here to bypass race conditions
        // fetchSites(initialId);
      }
    } catch (error) {
      console.error("❌ Error fetching customers:", error);
      setCustomers([]);
    }
  };

  // Run once on Component Mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // 2. State-driven Watcher: Listens for Dropdown State updates explicitly
  // useEffect(() => {
  //   console.log("🔄 useEffect detected selectedCustomer changed to:", selectedCustomer);
  //   if (selectedCustomer) {
  //     fetchSites(selectedCustomer);
  //   }
  // }, [selectedCustomer]);

  useEffect(() => {
  console.log("🔄 selectedCustomer changed:", selectedCustomer);

  if (!selectedCustomer) return;

  fetchSites(selectedCustomer);
}, [selectedCustomer]);

  const handleEditClick = (site) => {
    setSelectedSite(site);
    setEditOpen(true);
  };

  const handleCloseEdit = () => setEditOpen(false);

  const handleSaveEdit = (updatedSite) => {
    setRows(rows.map(r => r.id === updatedSite.id ? updatedSite : r));
    setEditOpen(false);
  };

  const handleDeleteClick = (site) => {
    setSiteToDelete(site);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteMessage("");
    setDeleteError(false);
  };

  const handleConfirmDelete = async () => {
    if (!siteToDelete) return;

    try {
      const assetId = siteToDelete.asset_id;

      const response = await apiService({
        url: `/api/delete_site/${assetId}/`,
        method: "DELETE",
        data: {},
      });

      const msg = response.data.message || "Site deleted successfully!";
      setDeleteMessage(msg);
      setDeleteError(false);
      toast.success(msg);

      setTimeout(() => {
        setRows(rows.filter(r => r.id !== siteToDelete.id));
        setDeleteOpen(false);
        setDeleteMessage("");
      }, 1500);

    } catch (error) {
      console.error("Error deleting site:", error);
      const backendMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete site.";

      setDeleteMessage(backendMessage);
      setDeleteError(true);
    }
  };

  return (
    <Box className="view-sites-container">
      <ToastContainer position="top-right" autoClose={3000} />

       <Box className="dropdown-button-row">
         <FormControl className="site-select-container" fullWidth sx={{ mb: 2 }}>
    <InputLabel id="customer-label">Select Customer</InputLabel>
    <Select
      labelId="customer-label"
      value={selectedCustomer}
      label="Select Customer"
      onChange={(e) => {
        const val = e.target.value;
        console.log("🕹️ Dropdown manually selected:", val);
        setSelectedCustomer(val);
      }}
    >
       <MenuItem value="all">All Customers</MenuItem>

      {customers.map((customer) => {
        const cId = customer.tb_customer_id;
        return (
          <MenuItem key={cId} value={cId}>
            {customer.customer_name || customer.name}
          </MenuItem>
        );
      })}
    </Select>
  </FormControl>
      </Box>

      <TableContainer component={Paper} className="site-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr No</TableCell>
              <TableCell>Site Name</TableCell>
              <TableCell>Survey No</TableCell>
              <TableCell>Pin Code</TableCell>
              <TableCell>Society Reg No</TableCell>
              <TableCell>Address</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Sites Are Available
                </TableCell>
              </TableRow>
            ) : (
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {row.name}
                      </Box>
                    </TableCell>
                    <TableCell>{row.survey_no}</TableCell>
                    <TableCell>{row.pin_code}</TableCell>
                    <TableCell>{row.society_reg_no}</TableCell>
                    <TableCell>{row.address}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleEditClick(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

      </TableContainer>
      
        <div className="pagination-bar">
          <div className="rows-select">
            Rows per page:
            <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
          </div>
          <div className="page-info">
            {rows.length > 0 ? page * rowsPerPage + 1 : 0}-
            {Math.min((page + 1) * rowsPerPage, rows.length)} of {rows.length}
          </div>
          <div className="page-arrows">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
            <button disabled={(page + 1) * rowsPerPage >= rows.length} onClick={() => setPage(page + 1)}>›</button>
          </div>
        </div>

      {selectedSite && (
        <EditSite
          open={editOpen}
          onClose={handleCloseEdit}
          site={selectedSite}
          onSave={handleSaveEdit}
        />
      )}

      <Dialog open={deleteOpen} onClose={handleCancelDelete} className="delete-dialog">
        <DialogTitle className="delete-dialog-title">Delete Site</DialogTitle>
        <DialogContent>
          <DialogContentText className="delete-dialog-text">
            {!deleteMessage && "Are you sure to delete this site?"}
          </DialogContentText>
          {deleteMessage && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: deleteError ? "#fdecea" : "#e8f5e9",
                color: deleteError ? "#d32f2f" : "#2e7d32",
                fontWeight: 500,
              }}
            >
              {deleteMessage}
            </Box>
          )}
        </DialogContent>
        <DialogActions className="delete-dialog-actions">
          {deleteError && deleteMessage ? (
            <Button onClick={handleCancelDelete} variant="contained" color="primary">OK</Button>
          ) : deleteMessage && !deleteError ? (
            <Button onClick={handleCancelDelete} variant="contained" color="success">Done</Button>
          ) : (
            <>
              <Button onClick={handleCancelDelete} variant="outlined" className="delete-cancel-btn">Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" color="error">Delete</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewSites;